/* ============================================================
   NP SPRITZ — decorative life-kit runtime.

   Every node this file creates is aria-hidden, pointer-events:none, and
   position:absolute inside an already-positioned parent. It therefore cannot
   shift layout, cannot be tabbed to, and cannot intercept a click. If this
   file never loads, the page is exactly the page it was before — that is the
   contract, and it is why the kit ships as an add-on rather than as edits
   spread across twenty liquid sections.

   Deterministic placement: positions come from a seeded PRNG keyed on the
   section's index, NOT Math.random(). Two loads of the same page put every
   sparkle in the same place. A decoration that teleports between reloads
   reads as a bug, and it also makes visual regression testing impossible.
   ============================================================ */
(function () {
  'use strict';
  if (window.__npSpritz) return;
  window.__npSpritz = true;

  var SVGNS = 'http://www.w3.org/2000/svg';
  var STAR_D = 'M12 0C13.1 7.4 16.6 10.9 24 12C16.6 13.1 13.1 16.6 12 24C10.9 16.6 7.4 13.1 0 12C7.4 10.9 10.9 7.4 12 0Z';
  var SQUIGGLE_D = 'M3 16C28 5 52 25 78 14S130 5 156 16 208 25 234 13 285 7 297 15';
  var WAVE_D = 'M0 0H1440V19C1305 45 1183 10 1032 26 881 42 761 7 611 21 461 35 331 5 191 19 121 26 61 33 0 26Z';

  /* ---- seeded PRNG (mulberry32). Same seed, same page, every time. ---- */
  function rng(seed) {
    var a = (seed >>> 0) + 0x6D2B79F5;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function svg(viewBox, d, opts) {
    opts = opts || {};
    var s = document.createElementNS(SVGNS, 'svg');
    s.setAttribute('viewBox', viewBox);
    s.setAttribute('aria-hidden', 'true');
    s.setAttribute('focusable', 'false');
    if (opts.stretch) s.setAttribute('preserveAspectRatio', 'none');
    if (opts.overflow) s.style.overflow = 'visible';
    if (d) {
      var p = document.createElementNS(SVGNS, 'path');
      p.setAttribute('d', d);
      if (opts.nonScalingStroke) p.setAttribute('vector-effect', 'non-scaling-stroke');
      s.appendChild(p);
    }
    return s;
  }

  function deco(cls) {
    var el = document.createElement('span');
    el.className = cls;
    el.setAttribute('aria-hidden', 'true');
    el.style.pointerEvents = 'none';
    return el;
  }

  function ensureRelative(el) {
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
  }

  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* =========================================================
     1 · SPARKLES — 4-point stars, 1-2 per section, slow twinkle.
     The cap is the whole design. Anchors live in the outer margin band of
     each section (x under 14% or over 84%) so a star never lands on top of
     a line of copy, which is the failure mode that makes this look cheap.
     ========================================================= */
  var ANCHORS = [
    { x: 8, y: 16 }, { x: 90.5, y: 21 }, { x: 88, y: 76 }, { x: 9.5, y: 71 },
    { x: 12, y: 44 }, { x: 92, y: 52 }, { x: 7, y: 88 }, { x: 89, y: 10 }
  ];
  var SPARKLE_SKIP = /^(legal-|shopify-section-footer)/;

  /* The page's band sections, in document order, across BOTH layouts this theme
     uses. The homepage builds itself out of Shopify sections
     (`.shopify-section > section`); every inner page template - how-it-works,
     faq, science, reviews, find-in-store - renders `<section class="sec">`
     straight into `<main>` with no section wrapper at all. Matching only the
     first shape is why the first live pass put sparkles and waves on the
     homepage and nothing anywhere else. Measured on the live inner pages, not
     assumed: /pages/how-it-works has nine bare `<section class="sec">` children
     of `<main>` and zero section-wrapped ones. */
  function topSections() {
    var list = $$('.shopify-section > section, .shopify-section > .np-ss,' +
                  ' main > section, main > .np-ss, #main > section, #main > .np-ss');
    var seen = [];
    return list.filter(function (s) {
      if (seen.indexOf(s) > -1) return false;
      seen.push(s);
      if (s.classList.contains('lgl')) return false;
      return s.offsetHeight > 80;
    });
  }

  function sparkleSections() {
    return topSections().filter(function (s) {
      if (s.id && SPARKLE_SKIP.test(s.id)) return false;
      if (s.querySelector(':scope > .sp-star')) return false;   /* already sparkled */
      return s.offsetHeight >= 220;
    });
  }

  function addSparkles() {
    var made = 0;
    sparkleSections().forEach(function (sec, i) {
      var r = rng(i * 9176 + 31);
      var n = r() > 0.42 ? 2 : 1;                 /* 1-2 per section, never more */
      ensureRelative(sec);
      var used = {};
      for (var k = 0; k < n; k++) {
        var ai;
        do { ai = Math.floor(r() * ANCHORS.length); } while (used[ai] && Object.keys(used).length < ANCHORS.length);
        used[ai] = 1;
        var a = ANCHORS[ai];
        var st = deco('sp-star');
        st.style.setProperty('--x', (a.x + (r() * 4 - 2)).toFixed(2) + '%');
        st.style.setProperty('--y', (a.y + (r() * 6 - 3)).toFixed(2) + '%');
        st.style.setProperty('--s', (19 + Math.round(r() * 17)) + 'px');
        st.style.setProperty('--r', Math.round(r() * 40 - 20) + 'deg');
        st.style.setProperty('--d', (r() * 4.5).toFixed(2) + 's');
        st.style.setProperty('--dur', (4.6 + r() * 3.4).toFixed(2) + 's');
        st.appendChild(svg('0 0 24 24', STAR_D));
        sec.appendChild(st);
        made++;
      }
    });
    return made;
  }

  /* =========================================================
     2 · SQUIGGLE UNDERLINES — hand-drawn stroke under the key word,
     drawn on scroll-in. Prefers an author-marked key word (.hl-sun /
     .hl-mark, which already exist in the liquid); falls back to the final
     word of the headline so a section that was never marked up still gets
     the signature move. .hl-calm is deliberately excluded — np.css:228
     already paints a highlight bar behind it.
     ========================================================= */
  var HEADS = 'h1.d1, h2.d2, h2.d3, h2.pr-h, h2.np-proofbar-t, .sec h2, .sec-sm h2, .np-ss h2';

  function wrapLastWord(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var last = null, node;
    while ((node = walker.nextNode())) if (node.nodeValue.trim()) last = node;
    if (!last) return null;
    var txt = last.nodeValue;
    var m = txt.match(/([^\s]+)(\s*)$/);
    if (!m || m[1].replace(/[^\w']/g, '').length < 3) return null;
    var idx = txt.length - m[0].length;
    var span = document.createElement('span');
    span.className = 'sp-sq';
    span.textContent = m[1];
    var after = document.createTextNode(m[2] || '');
    last.nodeValue = txt.slice(0, idx);
    last.parentNode.insertBefore(after, last.nextSibling);
    last.parentNode.insertBefore(span, after);
    return span;
  }

  function attachSquiggle(target) {
    if (!target || target.querySelector('.sp-sq-line')) return null;
    if (target.classList.contains('hl-calm')) return null;
    target.classList.add('sp-sq');
    var s = svg('0 0 300 24', SQUIGGLE_D, { stretch: true, overflow: true, nonScalingStroke: true });
    s.setAttribute('class', 'sp-sq-line');
    s.classList.add('sp-sq-line');
    target.appendChild(s);
    try {
      var len = s.querySelector('path').getTotalLength();
      if (len && isFinite(len)) target.style.setProperty('--len', Math.ceil(len));
    } catch (e) { /* getTotalLength unsupported — CSS fallback --len:320 stands */ }
    return target;
  }

  function addSquiggles() {
    var made = [];
    var seenHeads = [];
    /* pass A — author-marked key words */
    /* .hl-key (2026-09-01): neutral author-marked key word — squiggle only,
       no gold recolor / highlight bar. Used by np-avatar-swap.js so swapped
       avatar headlines get the squiggle under the KEY word instead of the
       pass-B fallback (final word) — Jared bug report: "Either."/"Drawer"/
       "Sedated." were squiggled instead of Calm/Honest/Steady. */
    $$('.hl-sun, .hl-mark, .hl-key').forEach(function (el) {
      var h = el.closest(HEADS.split(',').map(function (s) { return s.trim(); }).join(','));
      if (h) { if (seenHeads.indexOf(h) > -1) return; seenHeads.push(h); }
      if (attachSquiggle(el)) made.push(el);
    });
    /* pass B — unmarked section headlines get their final word squiggled */
    $$(HEADS).forEach(function (h) {
      if (seenHeads.indexOf(h) > -1) return;
      if (h.closest('.lgl')) return;
      /* data-sp-skip (2026-09-01): np-avatar-swap.js marks the product-name
         h2 so the only squiggle above the fold is the eyebrow's key word. */
      if (h.hasAttribute('data-sp-skip')) return;
      if (h.querySelector('.hl-sun, .hl-mark, .hl-calm, .sp-sq')) return;
      if (!h.textContent.trim() || h.textContent.trim().length < 8) return;
      seenHeads.push(h);
      var sp = wrapLastWord(h);
      if (sp && attachSquiggle(sp)) made.push(sp);
    });
    /* draw-on-scroll */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-drawn'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.4 });
      made.forEach(function (m) { io.observe(m); });
    } else {
      made.forEach(function (m) { m.classList.add('is-drawn'); });
    }
    return made.length;
  }

  /* =========================================================
     3 · BLOB BACKDROPS — soft brand-colour organic shapes behind the
     product art. Two placements: INSIDE the media wells (where the bottle
     is a transparent PNG on a gradient, so the blob reads as a halo) and
     BEHIND the photo cards (where the photo well is overflow:hidden, so
     the only place a blob can be seen is peeking past the card corners).
     ========================================================= */
  var BLOB_IN = [
    { sel: '.hero-media', bw: '86%', bh: '80%', bc: 'var(--sp-blob-a)', blur: '26px' },
    { sel: '.buy-media', bw: '84%', bh: '82%', bc: 'var(--sp-blob-c)', blur: '26px' },
    /* Cool tint, not the warm one. The final CTA card is --deep navy: a yellow
       blob at .26 alpha composites to olive sludge over it (seen in the first
       live capture, captures/spritz/f_final.png). A lazure blob reads as a
       glow behind the bottle, which is the effect this is for. */
    { sel: '.final-img', bw: '58%', bh: '50%', bc: 'rgba(122,196,236,.26)', blur: '34px', by: '54%' },
    { sel: '.ss-gummies', bw: '62%', bh: '58%', bc: 'var(--sp-blob-a)', blur: '38px' }
  ];
  /* Flat-background product wells on the inner pages (shop grid, scrollstats
     product still). Deliberately NOT .ridic-card: those cards are edge-to-edge
     PHOTO cards on a dark band, so a blob behind them is painted under the
     card's own white background and is invisible — that was measured, not
     assumed, and shipping an invisible decoration is worse than shipping none. */
  var BLOB_BEHIND = [
    { sel: '.card-img', bw: '88%', bh: '86%', bc: 'var(--sp-blob-a)', blur: '22px' },
    { sel: '.card-photo', bw: '88%', bh: '86%', bc: 'var(--sp-blob-b)', blur: '22px' },
    { sel: '.ss-product', bw: '80%', bh: '76%', bc: 'var(--sp-blob-c)', blur: '30px' }
  ];

  function addBlobs() {
    var made = 0;
    BLOB_IN.forEach(function (cfg, ci) {
      $$(cfg.sel).forEach(function (host, hi) {
        if (host.querySelector(':scope > .sp-blob')) return;
        ensureRelative(host);
        var b = deco('sp-blob');
        b.style.setProperty('--bw', cfg.bw);
        b.style.setProperty('--bh', cfg.bh);
        b.style.setProperty('--bc', cfg.bc);
        b.style.setProperty('--bblur', cfg.blur);
        if (cfg.by) b.style.setProperty('--by', cfg.by);
        b.style.setProperty('--bdur', (15 + ((ci + hi) % 5) * 1.7).toFixed(1) + 's');
        host.insertBefore(b, host.firstChild);
        made++;
      });
    });
    BLOB_BEHIND.forEach(function (cfg, ci) {
      $$(cfg.sel).forEach(function (host, i) {
        if (host.querySelector(':scope > .sp-blob')) return;
        ensureRelative(host);
        var b = deco('sp-blob');
        b.style.setProperty('--bw', cfg.bw);
        b.style.setProperty('--bh', cfg.bh);
        b.style.setProperty('--bc', cfg.bc);
        b.style.setProperty('--bblur', cfg.blur);
        b.style.setProperty('--bdur', (15 + ((ci + i) % 5) * 1.9).toFixed(1) + 's');
        host.insertBefore(b, host.firstChild);
        var img = host.querySelector(':scope > img, :scope > picture');
        if (img) { ensureRelative(img); img.style.zIndex = '1'; }
        made++;
      });
    });
    return made;
  }

  /* =========================================================
     4 · SCALLOPED FLOWER BADGE — the shape is the union of a rounded core
     rect and N circles walked around its perimeter, all filled in the same
     colour with no strokes, so the overlaps merge into one solid scallop.
     Computed rather than hand-drawn because the live consumer (.plan-flag)
     has a text-dependent width and a fixed hand-authored path would only
     fit one string.
     ========================================================= */
  function scallopSVG(w, h, petals) {
    var s = document.createElementNS(SVGNS, 'svg');
    s.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    s.setAttribute('preserveAspectRatio', 'none');
    s.setAttribute('aria-hidden', 'true');
    var g = document.createElementNS(SVGNS, 'g');
    var rad = Math.min(w, h) * 0.24;
    var core = document.createElementNS(SVGNS, 'rect');
    core.setAttribute('x', rad); core.setAttribute('y', rad);
    core.setAttribute('width', Math.max(1, w - rad * 2));
    core.setAttribute('height', Math.max(1, h - rad * 2));
    g.appendChild(core);
    /* walk the perimeter of the inset rect, dropping a petal every step */
    var iw = w - rad * 2, ih = h - rad * 2, per = 2 * (iw + ih);
    var n = petals || Math.max(8, Math.round(per / (rad * 1.45)));
    for (var i = 0; i < n; i++) {
      var t = (i / n) * per, cx, cy;
      if (t < iw) { cx = rad + t; cy = rad; }
      else if (t < iw + ih) { cx = rad + iw; cy = rad + (t - iw); }
      else if (t < 2 * iw + ih) { cx = rad + iw - (t - iw - ih); cy = rad + ih; }
      else { cx = rad; cy = rad + ih - (t - 2 * iw - ih); }
      var c = document.createElementNS(SVGNS, 'circle');
      c.setAttribute('cx', cx.toFixed(2)); c.setAttribute('cy', cy.toFixed(2));
      c.setAttribute('r', rad.toFixed(2));
      g.appendChild(c);
    }
    s.appendChild(g);
    return s;
  }

  function addBadges() {
    var made = 0;
    $$('.plan-flag, .np-shop-grid .badge, .card .badge').forEach(function (flag) {
      if (flag.classList.contains('sp-badge')) return;
      var r = flag.getBoundingClientRect();
      var w = Math.max(40, Math.round(r.width) + 26);
      var h = Math.max(20, Math.round(r.height) + 18);
      flag.classList.add('sp-badge');
      ensureRelative(flag);   /* never demotes an already-absolute flag */
      var bg = deco('sp-badge-bg');
      bg.appendChild(scallopSVG(w, h));
      flag.insertBefore(bg, flag.firstChild);
      made++;
    });
    return made;
  }

  /* =========================================================
     5 · RIBBON / SWOOSH — a big flowing band wrapping the product art.
     Sits at z-index 0 inside the media well; np-spritz.css lifts the
     bottle image to z-index 1 so the art always wins.
     ========================================================= */
  function addRibbons() {
    var made = 0;
    ['.hero-media', '.buy-media'].forEach(function (sel, si) {
      $$(sel).forEach(function (host) {
        if (host.querySelector(':scope > .sp-ribbon')) return;
        /* Skip a well whose art is an opaque photo filling the frame. The
           homepage hero is .hero-media--life, a full-bleed lifestyle shot: a
           ribbon behind it is painted and then completely covered, which is a
           decoration that exists only in the DOM. Verified against the live
           element capture (captures/spritz/live_hero_media.png) before this
           guard went in. */
        var art = host.querySelector(':scope > img');
        if (art) {
          var hr = host.getBoundingClientRect(), ar = art.getBoundingClientRect();
          if (hr.width && ar.width / hr.width > 0.9 && ar.height / hr.height > 0.9) return;
        }
        ensureRelative(host);
        var wrap = deco('sp-ribbon');
        var s = document.createElementNS(SVGNS, 'svg');
        s.setAttribute('viewBox', '0 0 600 480');
        s.setAttribute('preserveAspectRatio', 'none');
        s.setAttribute('aria-hidden', 'true');
        var gid = 'sp-rib-' + si + '-' + made;
        s.innerHTML =
          '<defs><linearGradient id="' + gid + '" x1="0" y1="0" x2="1" y2="1">' +
          '<stop offset="0" stop-color="#4DB1CF" stop-opacity=".38"/>' +
          '<stop offset="0.55" stop-color="#438FCA" stop-opacity=".26"/>' +
          '<stop offset="1" stop-color="#F7CC2F" stop-opacity=".30"/>' +
          '</linearGradient></defs>' +
          '<path fill="url(#' + gid + ')" d="M-40 318C90 240 168 372 288 322 408 272 470 150 640 196L640 300C470 268 420 372 300 404 180 436 92 372-40 420Z"/>' +
          '<path fill="url(#' + gid + ')" opacity=".55" d="M-40 132C110 78 190 196 320 152 450 108 520 44 640 76L640 142C520 118 452 178 330 220 208 262 110 200-40 232Z"/>';
        wrap.appendChild(s);
        host.insertBefore(wrap, host.firstChild);
        made++;
      });
    });
    return made;
  }

  /* =========================================================
     6 · WAVY SECTION DIVIDERS — replace the hard straight seam between two
     differently-coloured bands with a gentle wave. Painted INSIDE the lower
     section in the UPPER section's colour, because .band-deep is
     overflow:hidden (np.css:553) and would clip anything hanging above its
     own top edge.
     ========================================================= */
  function bgOf(el) {
    var n = el, guard = 0;
    while (n && n !== document.documentElement && guard++ < 6) {
      var c = getComputedStyle(n).backgroundColor;
      if (c && c !== 'transparent' && !/rgba\(0,\s*0,\s*0,\s*0\)/.test(c)) return c;
      n = n.parentElement;
    }
    return getComputedStyle(document.body).backgroundColor || '#fff';
  }

  function addWaves() {
    var secs = topSections();
    var made = 0;
    for (var i = 1; i < secs.length; i++) {
      var inner = secs[i];
      var a = bgOf(secs[i - 1]), b = bgOf(inner);
      if (a === b) continue;                         /* no seam to soften */
      if (inner.querySelector(':scope > .sp-wave')) continue;
      inner.classList.add('spritz-has-wave');
      ensureRelative(inner);
      var w = deco('sp-wave');
      w.style.setProperty('--sp-wave-fill', a);
      var s = svg('0 0 1440 52', WAVE_D, { stretch: true });
      w.appendChild(s);
      inner.insertBefore(w, inner.firstChild);
      made++;
    }
    return made;
  }

  /* =========================================================
     7 · MICRO-MOTION — card lift. The button sparkle-pop is pure CSS
     (np-spritz.css §7) and needs no wiring. .npfan-card is deliberately
     excluded: np-fan.liquid drives it with its own JS transform, and a
     second transform on hover would fight it.
     ========================================================= */
  function addLift() {
    var n = 0;
    /* .btn needs a positioning context for its ::after sparkle layer. Done here
       rather than in CSS so a button np.css has positioned absolutely keeps it. */
    $$('.btn').forEach(function (b) { ensureRelative(b); });
    $$('.ridic-card, .cx-card, .rev-card, .np-shop-grid .card, .cards > .card, .pat').forEach(function (c) {
      if (c.classList.contains('sp-lift')) return;
      if (c.closest('[data-npfan-stage]')) return;
      c.classList.add('sp-lift');
      n++;
    });
    return n;
  }

  /* ---------- run ---------- */
  function run() {
    var report = {};
    try { report.waves = addWaves(); } catch (e) { report.waves = 'ERR ' + e.message; }
    try { report.ribbons = addRibbons(); } catch (e) { report.ribbons = 'ERR ' + e.message; }
    try { report.blobs = addBlobs(); } catch (e) { report.blobs = 'ERR ' + e.message; }
    try { report.sparkles = addSparkles(); } catch (e) { report.sparkles = 'ERR ' + e.message; }
    try { report.squiggles = addSquiggles(); } catch (e) { report.squiggles = 'ERR ' + e.message; }
    try { report.badges = addBadges(); } catch (e) { report.badges = 'ERR ' + e.message; }
    try { report.lifts = addLift(); } catch (e) { report.lifts = 'ERR ' + e.message; }
    document.documentElement.classList.add('sp-on');
    /* Accumulate rather than replace. run() is called again at window.load and
       on shopify:section:load, and every add* function short-circuits on what it
       already built — so a bare assignment would overwrite the real first-pass
       counts with a row of zeros and read like a total failure. */
    var prev = window.__npSpritzReport || {};
    Object.keys(report).forEach(function (k) {
      var a = typeof prev[k] === 'number' ? prev[k] : 0;
      report[k] = typeof report[k] === 'number' ? a + report[k] : report[k];
    });
    window.__npSpritzReport = report;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  /* Second pass at window.load. At DOMContentLoaded the lazy images have not
     landed, so a tall section can still measure under the 220px sparkle floor
     and be skipped. Every add* function guards on what it already created, so
     re-running is additive-only, never duplicating. */
  window.addEventListener('load', function () { setTimeout(run, 120); }, { once: true });
  /* Shopify section re-render in the theme editor wipes injected nodes. */
  document.addEventListener('shopify:section:load', function () { setTimeout(run, 60); });
})();
